import React, { useState } from "react";
import { Mail, Phone, Send, User } from "lucide-react";
import { toast } from "sonner";
import { AdminModal } from "./AdminModal";
import { CodeCard } from "../../components/CodeCard";
import type { Representative } from "../../types";

export type RepFormValues = {
  fullName: string;
  position: string;
  phone: string;
  email: string;
  telegram: string;
  bio: string;
};

const emptyForm: RepFormValues = {
  fullName: "",
  position: "",
  phone: "",
  email: "",
  telegram: "",
  bio: "",
};

function fromRep(rep: Representative): RepFormValues {
  return {
    fullName: rep.fullName ?? "",
    position: rep.position ?? "",
    phone: rep.phone ?? "",
    email: rep.email ?? "",
    telegram: rep.telegram ?? "",
    bio: rep.bio ?? "",
  };
}

export function RepEditorModal({
  mode,
  initial,
  onClose,
  onSave,
  readOnly = false,
}: {
  mode: "create" | "view" | "edit";
  initial?: Representative;
  onClose: () => void;
  onSave: (data: RepFormValues) => Promise<void>;
  /** Exhibitor cabinet: view contacts/QR only, no edit CTA */
  readOnly?: boolean;
}) {
  const [form, setForm] = useState<RepFormValues>(initial ? fromRep(initial) : emptyForm);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(mode !== "view" && !readOnly);
  const title =
    mode === "create"
      ? "Добавить представителя"
      : editing
        ? "Редактировать представителя"
        : "Представитель";

  const fields: Array<{ key: keyof RepFormValues; label: string; placeholder: string }> = [
    { key: "fullName", label: "ФИО*", placeholder: "Иванов Иван Иванович" },
    { key: "position", label: "Должность", placeholder: "Директор по продажам" },
    { key: "phone", label: "Телефон", placeholder: "+7 (999) 000-00-00" },
    { key: "email", label: "Email", placeholder: "ivan@company.ru" },
    { key: "telegram", label: "Telegram", placeholder: "@username" },
  ];

  return (
    <AdminModal
      title={title}
      onClose={onClose}
      maxWidthClassName="max-w-lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100"
          >
            Закрыть
          </button>
          {!readOnly && !editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px]"
            >
              Изменить
            </button>
          ) : null}
          {!readOnly && editing ? (
            <button
              type="button"
              disabled={saving}
              onClick={async () => {
                if (!form.fullName.trim()) {
                  toast.error("Введите ФИО");
                  return;
                }
                setSaving(true);
                try {
                  await onSave(form);
                  toast.success(mode === "create" ? "Представитель добавлен" : "Данные сохранены");
                  onClose();
                } catch {
                  toast.error("Не удалось сохранить");
                } finally {
                  setSaving(false);
                }
              }}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] disabled:opacity-60"
            >
              {saving ? "Сохранение…" : mode === "create" ? "Добавить" : "Сохранить"}
            </button>
          ) : null}
        </>
      }
    >
      {initial?.photo ? (
        <img src={initial.photo} alt="" className="w-14 h-14 rounded-full object-cover mx-auto" />
      ) : (
        <div className="w-14 h-14 rounded-full bg-gray-100 mx-auto flex items-center justify-center">
          <User size={24} className="text-gray-400" />
        </div>
      )}

      {editing ? (
        <>
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-[11px] text-gray-500 mb-1 block">{f.label}</label>
              <input
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
              />
            </div>
          ))}
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Биография / заметка</label>
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 resize-none"
            />
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <div>
            <div className="text-[15px] text-gray-900">{form.fullName || "—"}</div>
            <div className="text-[13px] text-gray-500">{form.position || "Должность не указана"}</div>
          </div>
          <div className="rounded-xl bg-gray-50 p-3 space-y-1.5 text-[13px]">
            <div className="flex items-center gap-2 text-gray-700">
              <Phone size={13} className="text-gray-400" />
              {form.phone || <span className="text-gray-400">телефон не указан</span>}
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Mail size={13} className="text-gray-400" />
              {form.email || <span className="text-gray-400">email не указан</span>}
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Send size={13} className="text-gray-400" />
              {form.telegram || <span className="text-gray-400">telegram не указан</span>}
            </div>
          </div>
          {form.bio ? <p className="text-[12px] text-gray-500 leading-snug">{form.bio}</p> : null}
        </div>
      )}

      {initial ? (
        <CodeCard
          title="Бейдж"
          subtitle={
            initial.badgeBarcode
              ? "QR визитки · штрихкод с реплики или тестовый"
              : "QR визитки. Штрихкод появится, если есть в реплике Tezam (для теста генерируем сами)."
          }
          qrValue={initial.badgeQrCode}
          barcodeValue={initial.badgeBarcode}
          badge="Бейдж"
          badgeColor="bg-emerald-100 text-emerald-700"
          downloadName={initial.fullName}
        />
      ) : (
        <p className="text-[12px] text-sky-700 bg-sky-50 rounded-xl p-3">
          QR визитки создастся автоматически. Штрихкод бейджа для ручного представителя
          генерируем мы; у синхронизированных из Tezam — только код из реплики.
        </p>
      )}
    </AdminModal>
  );
}
